import {
    Button,
    Container,
    Dropdown,
    Form,
    FormGroup,
    Input,
    Segment,
    SegmentContent,
    SegmentHeader,
    Toggle,
} from '@/platform/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Deferred, Link, useForm, usePage } from '@inertiajs/react';
import { useMemo } from 'react';
import PermissionGrid from './components/PermissionGrid';

const CreateOrUpdateView = () => {
    const { role, permissions, roles } = usePage().props;
    const { data, setData, errors, post, patch, processing } = useForm({
        name: role?.name || '',
        reseller: role?.reseller || false,
        contact: role?.contact || false,
        permissions: (role?.permissions || []).map(permission => permission.id),
        // Only users who may assign any role receive the role options and can change this
        ...(roles && { assignable_roles: role?.assignable_roles || [] }),
    });

    const handleSubmit = () => {
        if (!role?.id) {
            post('/roles');

            return;
        }

        patch(`/roles/${role.id}`);
    };

    const togglePermission = permissionId => {
        setData(previous => {
            const assigned = previous?.permissions || [];

            return {
                ...previous,
                permissions: assigned.includes(permissionId)
                    ? assigned.filter(current => current !== permissionId)
                    : [...assigned, permissionId],
            };
        });
    };

    const title = useMemo(
        () => (role?.id ? `Wijzig rol: ${role.name}` : 'Nieuwe rol'),
        [role]
    );

    return (
        <Container>
            <Form onSubmit={handleSubmit}>
                <Segment>
                    <SegmentHeader header={title}>
                        <Button as={Link} href='/roles'>
                            <ArrowLeftIcon className='size-5' />
                            Terug
                        </Button>
                    </SegmentHeader>
                    <SegmentContent>
                        <FormGroup>
                            <Input
                                label='Rol naam'
                                required
                                value={data?.name || ''}
                                onChange={event =>
                                    setData('name', event?.target?.value)
                                }
                                error={errors?.name}
                            />
                            <Toggle
                                label='Reseller'
                                checked={!!data?.reseller}
                                disabled={!roles}
                                onChange={checked =>
                                    setData('reseller', checked)
                                }
                                error={errors?.reseller}
                            />
                            <Toggle
                                label='Contact'
                                checked={!!data?.contact}
                                disabled={!roles}
                                onChange={checked =>
                                    setData('contact', checked)
                                }
                                error={errors?.contact}
                            />
                            {roles && (
                                <Dropdown
                                    label='Toewijsbare rollen'
                                    description='Gebruikers met deze rol mogen deze rollen aan andere gebruikers toewijzen, en gebruikers met alleen deze rollen beheren.'
                                    multiple
                                    options={roles}
                                    value={data?.assignable_roles || []}
                                    onChange={value =>
                                        setData('assignable_roles', value)
                                    }
                                    error={errors?.assignable_roles}
                                />
                            )}
                        </FormGroup>
                    </SegmentContent>
                </Segment>

                <Deferred
                    data='permissions'
                    fallback={
                        <Segment>
                            <SegmentContent>
                                <p className='py-8 text-center text-sm text-gray-500'>
                                    Permissies worden geladen...
                                </p>
                            </SegmentContent>
                        </Segment>
                    }
                >
                    <PermissionGrid
                        permissions={permissions?.data || permissions || []}
                        assigned={data?.permissions}
                        onToggle={togglePermission}
                    />
                </Deferred>

                {errors?.permissions && (
                    <p className='mt-2 text-sm text-red-600'>
                        {errors.permissions}
                    </p>
                )}

                <Segment floating>
                    <SegmentContent right>
                        <Button
                            type='submit'
                            variant='primary'
                            loading={processing}
                        >
                            Opslaan
                        </Button>
                    </SegmentContent>
                </Segment>
            </Form>
        </Container>
    );
};

export default CreateOrUpdateView;
